import { useEffect, useState } from 'react';
import AvatarStack from '../Common/AvatarStack';
import useAppStore from '../../stores/app';

/**
 * ProctorsOnlineStack Component
 * 
 * Displays a vertical stack of avatars for all proctors currently online in the room.
 * Shows talking indicators for proctors who have their microphone active.
 * 
 * @component
 * @param {Object} props
 * @param {string} [props.talkingIndicatorPosition='end'] - Position of talking indicator
 * @param {boolean} [props.showTooltip=false] - Whether to show tooltips on hover
 * 
 * @example
 * <ProctorsOnlineStack />
 */
function ProctorsOnlineStack({
    talkingIndicatorPosition = 'end',
    showTooltip = true
}) {
    const [onlineProctors, setOnlineProctors] = useState([]);

    // Get all sessions from the store
    const proctors = useAppStore((state) => state.proctors);
    const userSid = useAppStore((state) => state.user.sid);

    useEffect(() => {
        if (proctors) {
            const sortedProctors = Object.values(proctors).sort((a, b) => {
                return new Date(b.entered) - new Date(a.entered);
            });

            // Collect all unique proctors across all sessions
            const proctorsOnline = [];

            Object.values(sortedProctors).forEach((proctor) => {
                // Omit yourself.
                if (proctor.sid !== userSid) {

                    // Only add if not already in map or update if talking status changed
                    proctorsOnline.push({
                        name: `${proctor.firstName} ${proctor.lastName}`,
                        isTalking: false,
                        entered: proctor.entered
                    });
                }
            });
            setOnlineProctors(proctorsOnline);
        }
    }, [proctors]);

    if (!onlineProctors || onlineProctors.length === 0) {
        return null;
    }

    return (
        <AvatarStack
            talkingIndicatorPosition={talkingIndicatorPosition}
            showTooltip={showTooltip}
            users={onlineProctors}
            renderTooltip={(user) => {
                return (
                    <div>
                        <div>{user.name}</div>
                        <div style={{ fontSize: '0.8em', opacity: 0.7 }}>
                            Joined: {new Date(user.entered).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </div>
                    </div>
                );
            }}
        />
    );
}

export default ProctorsOnlineStack;
